import { FormControl, FormHelperText, InputLabel, MenuItem, Select} from '@mui/material';
import { useForm } from '@/common/form/FormContext';
import { useFormField } from '@/common/form/hooks/useFormField';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { DateUtils } from '@/common/utils/DateUtils';

// Utility to build select options
const range = (start, end) =>
  Array.from({ length: end - start + 1 }, (_, i) =>
    i.toString().padStart(2, '0')
  );

const seconds = ['0']; // Fixed at 0 for simplicity
const minutes = ['*', ...range(0, 59)];
const hours = ['*', ...range(0, 23)];
const days = ['*', ...range(1, 31)];
const months = ['*', ...range(1, 12)];
const weekdays = ['*', ...range(0, 6)];

// Helper for generating a human-readable summary of a 6-field cron
const cronExprHelperText = (cron) => {
  const [s, m, h, d, mo, wd] = cron.split(' ');
  
  // Build the “time” part
  const time = 
    h === '*' && m === '*' && s === '*' 
      ? 'every second' 
    : h === '*' && m === '*'
      ? `every minute at second ${s.padStart(2, '0')}`
    : h === '*'
      ? `every hour at ${m.padStart(2, '0')} minute and ${s.padStart(2, '0')} second`
    : `${h.padStart(2, '0')}:${m.padStart(2, '0')}:${s.padStart(2, '0')}`;
  
  // Build the “day” part
  const day = 
    d === '*' && wd === '*' 
      ? 'every day' 
    : d !== '*' 
      ? `on day ${d} of the month` 
      : `on weekday ${wd}`;
  
  // Optional month
  const month = mo === '*' ? '' : ` in month ${mo}`;
  
  return `Runs ${time}, ${day}${month}`;
};

export const CronExprFormField = () => {
  const { formValues, assignValue } = useForm();
  const { onBlur, error } = useFormField(
    'cronExpr',
    ['cronExpr'],
    () => {
      if (!formValues.cronExpr) return 'A CRON expression is required';
    },
    'CRON job schedule'
  );

  const { entityFormMode } = useEntityMgr();
  const disabled = entityFormMode === EntityFormMode.VIEW;

  const cronParts = (formValues.cronExpr?.split(' ') ?? [
    '0',
    '0',
    '*',
    '*',
    '*',
    '*',
  ]).slice(0, 6);

  const updatePart = (index, value) => {

    // Also set the TimeZone
    assignValue('timeZone', DateUtils.timeZoneName());

    const updated = [...cronParts];
    updated[index] = value;
    assignValue('cronExpr', updated.join(' '));
  };

  const partConfigs = [
    { label: 'Seconds', values: seconds },
    { label: 'Minutes', values: minutes },
    { label: 'Hours', values: hours },
    { label: 'Day', values: days },
    { label: 'Month', values: months },
    { label: 'Weekday', values: weekdays },
  ];

  return (
    <FormControl fullWidth error={!!error} disabled={disabled}>
      <InputLabel shrink>Cron Expression</InputLabel>
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        {partConfigs.map((part, idx) => (
          <Select
            key={part.label}
            value={cronParts[idx]}
            onChange={(e) => updatePart(idx, e.target.value)}
            onBlur={onBlur}
            displayEmpty
            size="small"
            variant="outlined"
          >
            {part.values.map((val) => (
              <MenuItem key={val} value={val}>
                {val}
              </MenuItem>
            ))}
          </Select>
        ))}
      </div>
      <FormHelperText>
        {error || cronExprHelperText(cronParts.join(' '))}
      </FormHelperText>
    </FormControl>
  );
};
